// ===========================================
// The school's current week — the default period for the report pages
// ===========================================
// A report opened with no filter used to show a "select filters" card, which
// reads as "there is no data". The pages now default to the school's CURRENT
// week, Monday to Sunday (term weeks are Monday-anchored — CLAUDE.md), and say
// so in the heading, leading with the term week when one applies.
//
// "Today" is the SCHOOL's today (Intl with the school's zone), never the
// browser's: an admin checking from a different zone must see the same week
// the teachers see. Every date here is a YYYY-MM-DD key, never an instant.

import { currentWeekNumber, formatUTCDate } from './dateUtils';

export interface DateRangeKeys {
  startDate: string;
  endDate: string;
}

export interface TermLike {
  id?: string;
  name: string;
  startDate: string;
  endDate: string;
}

/** The school's calendar day for `now`, as YYYY-MM-DD. */
export function schoolTodayKey(timeZone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Shift a YYYY-MM-DD key by whole days, in UTC so no zone ever leaks in. */
export function shiftDateKey(key: string, days: number): string {
  const d = new Date(`${key}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Monday → Sunday of the school week containing `now`. */
export function schoolWeekKeys(timeZone: string, now: Date = new Date()): DateRangeKeys {
  const today = schoolTodayKey(timeZone, now);
  const dow = new Date(`${today}T00:00:00.000Z`).getUTCDay(); // 0 = Sunday
  const startDate = shiftDateKey(today, -((dow + 6) % 7));
  return { startDate, endDate: shiftDateKey(startDate, 6) };
}

const DD_MM_YYYY: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

/**
 * "Week 8 · 07/09/2026 – 13/09/2026" when the range is exactly one Monday-anchored
 * week inside a term (CLAUDE.md Critical Rule 7: lead with the term week), else
 * "07/09/2026 – 13/09/2026". Both ends inclusive, dd/mm/yyyy.
 */
export function describeDateRange(range: DateRangeKeys, terms: readonly TermLike[] = []): string {
  const dates = `${formatUTCDate(range.startDate, DD_MM_YYYY, 'en-AU')} – ${formatUTCDate(range.endDate, DD_MM_YYYY, 'en-AU')}`;
  const isMonday = new Date(`${range.startDate}T00:00:00.000Z`).getUTCDay() === 1;
  const isOneWeek = shiftDateKey(range.startDate, 6) === range.endDate;
  if (!isMonday || !isOneWeek) return dates;
  // OVERLAP, not containment. Term weeks are Monday-anchored, so a term that starts
  // mid-week (the Tuesday after a public holiday) has a week 1 beginning the Monday
  // BEFORE its start date. Requiring term.startDate <= weekMonday dropped the "Week 1"
  // label for every such term. (/qa 2026-09-10)
  const term = terms.find(
    (t) => t.startDate.slice(0, 10) <= range.endDate && range.startDate <= t.endDate.slice(0, 10)
  );
  if (!term) return dates;
  const week = currentWeekNumber(term.startDate, new Date(`${range.startDate}T00:00:00.000Z`));
  return `Week ${week} · ${dates}`;
}

/**
 * The term a school is IN today; failing that, the next one to start; failing
 * that, the most recent one. Reports that need a term (hybrid booking metrics
 * require one — the API answers 400 without it) default to this rather than to
 * nothing. Dates compare as YYYY-MM-DD keys, both ends inclusive.
 */
export function pickCurrentTerm<T extends TermLike>(terms: readonly T[], todayKey: string): T | undefined {
  const key = (v: string) => v.slice(0, 10);
  const containing = terms.find((t) => key(t.startDate) <= todayKey && todayKey <= key(t.endDate));
  if (containing) return containing;
  const upcoming = [...terms].filter((t) => key(t.startDate) > todayKey).sort((a, b) => key(a.startDate).localeCompare(key(b.startDate)));
  if (upcoming.length) return upcoming[0];
  const past = [...terms].filter((t) => key(t.endDate) < todayKey).sort((a, b) => key(b.endDate).localeCompare(key(a.endDate)));
  return past[0];
}

/** "Term 3 2026 · 20/07/2026 – 27/09/2026" */
export function describeTerm(term: TermLike): string {
  return `${term.name} · ${formatUTCDate(term.startDate, DD_MM_YYYY, 'en-AU')} – ${formatUTCDate(term.endDate, DD_MM_YYYY, 'en-AU')}`;
}
